import { Link } from "react-router-dom";
import { ShieldCheck, AlertTriangle, ExternalLink } from "lucide-react";

/* LastCheckedBadge — shows when a time-sensitive public page (visa rules,
   airport procedures, entry requirements, fees) was last verified and whether
   it's inside its review window. Phase 2 content governance requirement.
   Pass either an ISO date string or a full {source_url, source_org, last_verified, review_days?} object. */
export default function LastCheckedBadge({
  lastVerified,
  sourceUrl,
  sourceOrg,
  reviewDays = 180,
  compact = false,
  className = "",
}) {
  if (!lastVerified) return null;

  const d = new Date(lastVerified);
  if (Number.isNaN(d.getTime())) return null;

  const ageMs = Date.now() - d.getTime();
  const ageDays = Math.floor(ageMs / 86400_000);
  const stale = ageDays > reviewDays;
  const reviewDue = new Date(d.getTime() + reviewDays * 86400_000);

  const dateFmt = d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  const reviewFmt = reviewDue.toLocaleDateString(undefined, { year: "numeric", month: "short" });

  const tint = stale
    ? "bg-amber-50 border-amber-300 text-amber-900"
    : "bg-emerald-50 border-emerald-200 text-emerald-900";
  const Icon = stale ? AlertTriangle : ShieldCheck;

  if (compact) {
    return (
      <span className={`inline-flex items-center gap-1 text-[11px] font-700 px-2 py-0.5 rounded-full border ${tint} ${className}`}
        title={`Last verified ${dateFmt}${stale ? " — review overdue" : ""}`}
        data-testid="last-checked-badge-compact">
        <Icon className="w-3 h-3" /> {stale ? "Review overdue" : `Checked ${dateFmt}`}
      </span>
    );
  }

  return (
    <div className={`border rounded-lg p-3 text-xs ${tint} ${className}`} data-testid="last-checked-badge">
      <div className="flex items-start gap-2">
        <Icon className="w-4 h-4 shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <div className="font-800">
            {stale ? "This page is overdue for review" : "Verified against the source"}
          </div>
          <div className="opacity-90 mt-0.5">
            Last checked <b>{dateFmt}</b> ({ageDays} day{ageDays === 1 ? "" : "s"} ago).
            {" "}Next review by <b>{reviewFmt}</b>.
          </div>
          {sourceOrg && (
            <div className="mt-1 inline-flex items-center gap-1">
              <span className="opacity-70">Source:</span>
              {sourceUrl ? (
                <a href={sourceUrl} target="_blank" rel="noopener noreferrer"
                  className="font-700 inline-flex items-center gap-0.5 hover:underline"
                  data-testid="last-checked-source">
                  {sourceOrg} <ExternalLink className="w-3 h-3" />
                </a>
              ) : <span className="font-700">{sourceOrg}</span>}
            </div>
          )}
          {stale && (
            <div className="mt-2">
              <Link to="/contact" className="font-700 underline" data-testid="last-checked-report">
                Spot something out of date? Tell us
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
